import React, { useLayoutEffect, useRef } from 'react';
import { Location, NavigationType, useLocation, useNavigationType } from 'react-router-dom';
import { getCachedBottomNav } from '../api/ProductAPI';
import { consumeInAppBack, wasInAppBack } from '../utils/navigation';

/**
 * Animated page changes: the incoming page itself slides/fades in, animated with the Web
 * Animations API on transform + opacity only, so it runs on the compositor.
 *
 *  - Tab → tab (the bottom-nav destinations):  short directional slide + fade. Moving right
 *    enters from the right, moving left from the left.
 *  - Tab ⇄ feature (Home tile → Visitors, and back):  the same soft directional slide.
 *  - Feature → deeper detail:  push (enters from the right). Detail → feature, or
 *    browser/hardware back:  pop (enters from the left).
 *
 * The page switches the moment the route changes and animates from its first frame. It used to
 * be a View Transition, which froze the old page until the new one was ready (up to 600 ms) and
 * took full-page snapshots on every tap — fine on iOS, but it read as lag on Android WebViews.
 * The background chunk preloading in Routes means the new page's code is usually already there;
 * while its data loads, the page's own loader/skeleton slides in.
 *
 * Only the routed page is wrapped. The bottom nav lives outside this component, so it stays put.
 *
 * Fixed-position children (FABs, sticky headers) lay out against the wrapper while it carries a
 * transform. So the slide only runs from the top of the page, with the wrapper clipped to one
 * viewport for its few hundred ms — fixed children then land exactly where they will rest. When
 * the window is already scrolled, the page fades in instead (opacity doesn't move fixed children).
 *
 * Native back gestures: the iOS WKWebView edge-swipe (allowsBackForwardNavigationGestures) has
 * already animated the page away by the time popstate fires, so a POP gets no web animation when:
 *  - the browser says it already showed one (`PopStateEvent.hasUAVisualTransition`), or
 *  - we're in iOS WebKit and the POP wasn't started by one of our own back controls
 *    (utils/navigation `goBack` marks those) — i.e. assume a native edge-swipe.
 * Android hardware back (a POP, not iOS) and in-app back arrows keep their slide.
 */

// Flip to false to turn all page transitions off without touching anything else.
const ROUTE_TRANSITIONS_ENABLED = true;

export type TransitionKind = 'tab-fwd' | 'tab-back' | 'feature-in' | 'feature-out' | 'push' | 'pop';

const tabIndex = (pathname: string, tabs: string[]): number =>
  tabs.findIndex(t => pathname === t || pathname.startsWith(`${t}/`));

export function pickTransition(prev: Location, next: Location, navType: NavigationType): TransitionKind | null {
  if (prev.pathname === next.pathname) return null;      // search/state-only change
  if (navType === 'REPLACE') return null;                // redirects, auth bounces
  const tabs = (getCachedBottomNav() ?? []).filter(i => !i.locked).map(i => i.deeplink);
  const a = tabIndex(prev.pathname, tabs);
  const b = tabIndex(next.pathname, tabs);
  if (a >= 0 && b >= 0) return b > a ? 'tab-fwd' : 'tab-back';
  // Opening a feature from a tab (Home's Visitors / Complaint / Address tiles…) and coming back
  // use the same soft directional slide as the bottom-nav tabs, not a full-screen push. "Arriving at a
  // tab from a non-tab" is the way back (history pop, or a post-save redirect to the tab root).
  if (b >= 0 && a < 0) return 'feature-out';
  if (a >= 0 && b < 0) return 'feature-in';
  return navType === 'POP' ? 'pop' : 'push';
}

const EASE_OUT_EXPO = 'cubic-bezier(0.22, 1, 0.36, 1)';
const EASE_IOS = 'cubic-bezier(0.32, 0.72, 0, 1)';

const slide = (fromX: string, duration: number, easing: string) => ({
  keyframes: [
    { opacity: 0, transform: `translate3d(${fromX}, 0, 0)` },
    { opacity: 1, transform: 'translate3d(0, 0, 0)' },
  ],
  timing: { duration, easing },
});

const SLIDES: Record<TransitionKind, ReturnType<typeof slide>> = {
  'tab-fwd': slide('40px', 320, EASE_OUT_EXPO),
  'feature-in': slide('40px', 320, EASE_OUT_EXPO),
  'tab-back': slide('-40px', 320, EASE_OUT_EXPO),
  'feature-out': slide('-40px', 320, EASE_OUT_EXPO),
  push: slide('30%', 340, EASE_IOS),
  pop: slide('-20%', 300, EASE_IOS),
};

const FADE = { keyframes: [{ opacity: 0 }, { opacity: 1 }], timing: { duration: 200, easing: 'ease-out' } };

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Recorded in the capture phase, i.e. before react-router's own popstate listener updates the
// location, so the effect below sees the flags of the POP it is handling.
let lastPopHadUAVisualTransition = false;
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', (e: PopStateEvent) => {
    lastPopHadUAVisualTransition = (e as PopStateEvent & { hasUAVisualTransition?: boolean }).hasUAVisualTransition === true;
  }, { capture: true });
}

const isIOSWebKit = (): boolean =>
  /iPhone|iPad|iPod/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// True when the platform has already animated this POP (see the header comment).
function popAlreadyAnimated(): boolean {
  if (lastPopHadUAVisualTransition) return true;
  // Even where the flag exists, WKWebView may report false for its edge-swipe, so on iOS any POP
  // not started by our own back control is treated as native.
  return isIOSWebKit() && !wasInAppBack();
}

const RouteTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navType = useNavigationType();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef<Location>(location);
  const activeRef = useRef<Animation | null>(null);

  // Layout effect: runs after the new page is in the DOM but before the browser paints it, so
  // the first frame the user sees is already the animation's first keyframe.
  useLayoutEffect(() => {
    const prev = prevRef.current;
    if (location === prev) return;
    prevRef.current = location;

    let skipForNativePop = false;
    if (navType === 'POP') {
      skipForNativePop = popAlreadyAnimated();
      consumeInAppBack();
      lastPopHadUAVisualTransition = false;
    }

    activeRef.current?.cancel();
    activeRef.current = null;

    const el = wrapperRef.current;
    const kind = ROUTE_TRANSITIONS_ENABLED && el && typeof el.animate === 'function'
      && !prefersReducedMotion() && !skipForNativePop
      ? pickTransition(prev, location, navType)
      : null;
    if (!kind || !el) return;

    const atTop = window.scrollY <= 1;
    const { keyframes, timing } = atTop ? SLIDES[kind] : FADE;
    if (atTop) { el.style.height = '100vh'; el.style.overflow = 'hidden'; }
    const anim = el.animate(keyframes, timing);
    activeRef.current = anim;
    const done = () => {
      if (activeRef.current === anim) activeRef.current = null;
      el.style.height = '';
      el.style.overflow = '';
    };
    anim.onfinish = done;
    anim.oncancel = done;
  }, [location, navType]);

  return <div ref={wrapperRef}>{children}</div>;
};

export default RouteTransition;
